import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';
import type { OrderHistoryViewerQuery } from '@/__generated__/OrderHistoryViewerQuery.graphql';
import { OrderHistoryViewerQuery as OrderHistoryViewerQueryNode } from '@/features/order/graphql/OrderHistoryViewerQuery';
import type { RouteObject, Params } from 'react-router-dom';
import type { Environment } from 'relay-runtime';
import { AppLayout } from '@/components/layout/AppLayout';
import { preloadViewer, preloadRestaurantDashboard } from '@/relay/preload';

// Lazy load pages
const PlansPage = lazy(() => import('@/features/subscription/pages/PlansPage'));
const NotFoundPage = lazy(() => import('@/features/public/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const ResetPasswordPage = lazy(() => import('@/features/restaurant/pages/ResetPasswordPage'));

// Lazy load features
const KdsPage = lazy(() => import('@/features/kds/pages/KdsPage'));
const RestaurantLoginRoute = lazy(() => import('@/features/restaurant/Route').then(m => ({ default: m.RestaurantLoginRoute })));
const RestaurantDashboardRoute = lazy(() => import('@/features/restaurant/Route').then(m => ({ default: m.RestaurantDashboardRoute })));
const RestaurantAccountRoute = lazy(() => import('@/features/restaurant/Route').then(m => ({ default: m.RestaurantAccountRoute })));
const RestaurantRegisterRoute = lazy(() => import('@/features/restaurant/Route').then(m => ({ default: m.RestaurantRegisterRoute })));
const VerifyEmailRoute = lazy(() => import('@/features/restaurant/Route').then(m => ({ default: m.VerifyEmailRoute })));
const RequestPasswordResetRoute = lazy(() => import('@/features/restaurant/Route').then(m => ({ default: m.RequestPasswordResetRoute })));
const MyCardsPage = lazy(() => import('@/features/subscription/pages/MyCardsPage').then(m => ({ default: m.MyCardsPage })));
const ExcessChargesPage = lazy(() => import('@/features/subscription/pages/ExcessChargesPage').then(m => ({ default: m.ExcessChargesPage })));

// Redirect to /restaurante/painel if logged in, /login if not
const AuthRedirect = () => {
  const data = useLazyLoadQuery<OrderHistoryViewerQuery>(OrderHistoryViewerQueryNode, {}, { fetchPolicy: 'store-and-network' });
  const role = data.viewer.user?.role;
  const isAuthenticated = role === 'RESTAURANT' || role === 'ADMIN';
  return <Navigate to={isAuthenticated ? '/restaurante/painel' : '/login'} replace />;
};

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
    <div>Carregando...</div>
  </div>
);

export type RoutePreload = (environment: Environment, params: Params<string>) => Promise<unknown>;

export type RouteHandle = {
  preload?: RoutePreload;
};

export const routes: RouteObject[] = [
  {
    path: 'kds/:token',
    element: <Suspense fallback={<PageLoader />}><KdsPage /></Suspense>,
  },
  {
    path: '/',
    element: <AppLayout />,
    handle: {
      preload: (environment: Environment, _params: unknown) => preloadViewer(environment)
    },
    children: [
      { index: true, element: <Suspense fallback={<PageLoader />}><AuthRedirect /></Suspense> },
      {
        path: 'login',
        element: <Suspense fallback={<PageLoader />}><RestaurantLoginRoute /></Suspense>
      },
      {
        path: 'planos',
        element: <Suspense fallback={<PageLoader />}><PlansPage /></Suspense>
      },
      {
        path: 'criar-conta',
        element: <Suspense fallback={<PageLoader />}><RestaurantRegisterRoute /></Suspense>
      },
      {
        path: 'verificar-email',
        element: <Suspense fallback={<PageLoader />}><VerifyEmailRoute /></Suspense>
      },
      {
        path: 'redefinir-senha',
        element: <Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>
      },
      {
        path: 'esqueci-senha',
        element: <Suspense fallback={<PageLoader />}><RequestPasswordResetRoute /></Suspense>
      },
      {
        path: 'restaurante',
        element: <Suspense fallback={<PageLoader />}><AuthRedirect /></Suspense>
      },
      {
        path: 'restaurante/painel',
        element: <Suspense fallback={<PageLoader />}><RestaurantDashboardRoute /></Suspense>,
        handle: {
          preload: (environment: Environment, _params: unknown) => preloadRestaurantDashboard(environment)
        }
      },
      {
        path: 'restaurante/minha-conta',
        element: <Suspense fallback={<PageLoader />}><RestaurantAccountRoute /></Suspense>
      },
      {
        path: 'restaurante/meus-cartoes',
        element: <Suspense fallback={<PageLoader />}><MyCardsPage /></Suspense>
      },
      {
        path: 'restaurante/cobrancas-excedentes',
        element: <Suspense fallback={<PageLoader />}><ExcessChargesPage /></Suspense>
      },
      {
        path: '*',
        element: <Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>
      }
    ]
  }
];

export const AppRoutes = () => {
  return useRoutes(routes);
};
